import React from 'react';
import { Button, Switch, Tooltip, InputNumber } from 'antd';
import { PlusOutlined, MinusOutlined, CheckOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import type { EventWithCircuit } from '../../api/eventsService';
import { isEventActive as checkEventActive } from '../../utils/eventSource';
import { isTioProvider, type MapTileProvider } from '../../pages/Nowcasting/useNowcastingState';

dayjs.extend(utc);
dayjs.extend(timezone);

export interface TimelineTitleProps {
  selectedEvent: EventWithCircuit;
  theme: 'light' | 'dark';
  mode: 'range' | 'point';
  selectedRange: { start: Date; end: Date } | null;
  selectedPoint: Date | null;
  setSelectedRange: React.Dispatch<React.SetStateAction<{ start: Date; end: Date } | null>>;
  setSelectedPoint: React.Dispatch<React.SetStateAction<Date | null>>;
  isLiveMode: boolean;
  setIsLiveMode: React.Dispatch<React.SetStateAction<boolean>>;
  pendingStartMinutes: number | null;
  setPendingStartMinutes: React.Dispatch<React.SetStateAction<number | null>>;
  pendingEndMinutes: number | null;
  setPendingEndMinutes: React.Dispatch<React.SetStateAction<number | null>>;
  adjustStartTime: (minutes: number) => void;
  adjustEndTime: (minutes: number) => void;
  liveStartMinLimit: number;
  liveStartMinutes: number;
  liveEndMinutes: number;
  liveEndMinutesForRadar: number;
  mapTileProvider: MapTileProvider;
  onLiveStartMinutesChange?: (minutes: number) => void;
  onLiveEndMinutesChange?: (minutes: number) => void;
  onLiveEndMinutesForRadarChange?: (minutes: number) => void;
  onDateRangeChange?: (startDate: Date, endDate: Date) => void;
  onDatePointChange?: (date: Date) => void;
  onRefresh?: () => void;
  refreshing?: boolean;
  compact?: boolean;
}

const timeControlButtonStyle: React.CSSProperties = {
  width: '20px',
  height: '16px',
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '8px',
};

const timeControlButtonStyleCompact: React.CSSProperties = {
  width: '16px',
  height: '12px',
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '7px',
};

export const TimelineTitle: React.FC<TimelineTitleProps> = ({
  selectedEvent,
  theme,
  mode,
  selectedRange,
  selectedPoint,
  setSelectedRange,
  setSelectedPoint,
  isLiveMode,
  setIsLiveMode,
  pendingStartMinutes,
  setPendingStartMinutes,
  pendingEndMinutes,
  setPendingEndMinutes,
  adjustStartTime,
  adjustEndTime,
  liveStartMinLimit,
  liveStartMinutes,
  liveEndMinutes,
  liveEndMinutesForRadar,
  mapTileProvider,
  onLiveStartMinutesChange,
  onLiveEndMinutesChange,
  onLiveEndMinutesForRadarChange,
  onDateRangeChange,
  onDatePointChange,
  onRefresh,
  refreshing = false,
  compact = false,
}) => {
  const tz = selectedEvent?.circuit?.time_zone || 'UTC';
  const isEventActive = checkEventActive(selectedEvent);

  const formatCalendarDate = (date: Date) =>
    dayjs(date).tz(tz).format('MMM D, YYYY, HH:mm');

  const handleEnableLiveRange = () => {
    const now = new Date();
    const startMinutes = Math.max(liveStartMinLimit, Math.min(0, liveStartMinutes));
    const endMinutes =
      isTioProvider(mapTileProvider)
        ? Math.max(1, Math.min(60, liveEndMinutes))
        : Math.max(0, Math.min(60, liveEndMinutesForRadar));
    const start = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      now.getHours(),
      now.getMinutes() + startMinutes,
      0,
      0
    );
    const end = new Date(now.getTime() + endMinutes * 60 * 1000);
    setSelectedRange({ start, end });
    onDateRangeChange?.(start, end);
  };

  const handleEnableLivePoint = () => {
    const now = new Date();
    setSelectedPoint(now);
    onDatePointChange?.(now);
  };

  const liveToggleBg = isLiveMode
    ? theme === 'dark'
      ? 'rgba(255, 77, 79, 0.15)'
      : 'rgba(255, 77, 79, 0.1)'
    : 'transparent';

  const liveIndicatorStyle: React.CSSProperties = {
    width: compact ? '6px' : '8px',
    height: compact ? '6px' : '8px',
    borderRadius: '50%',
    backgroundColor: isLiveMode
      ? '#ff4d4f'
      : theme === 'dark'
        ? 'rgba(255, 255, 255, 0.25)'
        : 'rgba(0, 0, 0, 0.15)',
    animation: isLiveMode ? 'live-pulse 1.5s ease-in-out infinite' : 'none',
  };

  const liveLabelStyle: React.CSSProperties = {
    fontSize: compact ? '9px' : '11px',
    fontWeight: isLiveMode ? 600 : 400,
    color: isLiveMode
      ? '#ff4d4f'
      : theme === 'dark'
        ? 'rgba(255, 255, 255, 0.45)'
        : 'rgba(0, 0, 0, 0.35)',
    letterSpacing: '0.5px',
  };

  const timeDisplayStyle: React.CSSProperties = {
    fontSize: compact ? '10px' : '12px',
    fontWeight: 'normal',
    color: theme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)',
    backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
    padding: compact ? '1px 6px' : '4px 12px',
    borderRadius: '4px',
  };

  const btnStyle = compact ? timeControlButtonStyleCompact : timeControlButtonStyle;

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: compact ? '4px' : '8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: compact ? '8px' : '16px' }}>
        <span style={compact ? { fontSize: '12px' } : undefined}>Event Timeline</span>

        {mode === 'range' && selectedRange && (
          <div style={{ display: 'flex', alignItems: 'center', gap: compact ? '4px' : '8px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? '1px' : '2px' }}>
              <Button
                icon={<PlusOutlined />}
                size="small"
                type="text"
                onClick={() => adjustStartTime(1)}
                style={btnStyle}
                title="Add 1 minute to start time"
              />
              <Button
                icon={<MinusOutlined />}
                size="small"
                type="text"
                onClick={() => adjustStartTime(-1)}
                style={btnStyle}
                title="Subtract 1 minute from start time"
              />
            </div>

            <span style={timeDisplayStyle}>
              {formatCalendarDate(selectedRange.start)} - {formatCalendarDate(selectedRange.end)}
              <span style={{ opacity: 0.6, marginLeft: '4px', fontSize: compact ? '8px' : '10px' }}>
                ({selectedEvent?.circuit?.time_zone?.split('/')[1] || 'Local'})
              </span>
            </span>

            <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? '1px' : '2px' }}>
              <Button
                icon={<PlusOutlined />}
                size="small"
                type="text"
                onClick={() => adjustEndTime(1)}
                style={btnStyle}
                title="Add 1 minute to end time"
              />
              <Button
                icon={<MinusOutlined />}
                size="small"
                type="text"
                onClick={() => adjustEndTime(-1)}
                style={btnStyle}
                title="Subtract 1 minute from end time"
              />
            </div>

            {isEventActive && (
              <>
                <div
                  style={{
                    width: '1px',
                    height: compact ? '16px' : '24px',
                    backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                    margin: compact ? '0 2px' : '0 4px',
                  }}
                />
                <Tooltip title="Live mode automatically updates the time range every minute. 'From' sets how many minutes before current time to start (negative value). 'To' sets how many minutes ahead to show (only for Tomorrow.io source). Radar: Observations uses current time; Nowcasting uses 30 min ahead.">
                  <div style={{ display: 'flex', alignItems: 'center', gap: compact ? '4px' : '8px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? '1px' : '2px', alignItems: 'center' }}>
                      <label
                        style={{
                          fontSize: compact ? '8px' : '9px',
                          color: theme === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}
                      >
                        From (MIN)
                      </label>
                      <InputNumber
                        size="small"
                        min={liveStartMinLimit}
                        max={0}
                        value={pendingStartMinutes ?? undefined}
                        onChange={(value) => {
                          setPendingStartMinutes(
                            value === null ? null : Math.max(liveStartMinLimit, Math.min(0, value))
                          );
                        }}
                        disabled={!isLiveMode}
                        style={{ width: compact ? '40px' : '50px', fontSize: compact ? '9px' : '11px' }}
                        controls={false}
                        formatter={(value) => `${value}`}
                        parser={(value) => {
                          const parsed = parseInt(value?.replace(/[^\d-]/g, '') || '0', 10);
                          return Math.max(liveStartMinLimit, Math.min(0, parsed));
                        }}
                      />
                    </div>

                    {(mapTileProvider === 'radar' || mapTileProvider === 'radar-l2' || mapTileProvider === 'radar-l2-numeric') && onLiveEndMinutesForRadarChange && (
                      <Tooltip
                        title={
                          liveEndMinutesForRadar !== 0
                            ? 'Nowcast on: range end is +30 min (supports +5…+29 min tiles). Turn off for current time only.'
                            : 'Nowcast off: range end is current time. Turn on for +30 min (nowcasting tiles).'
                        }
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: compact ? '3px' : '6px' }}>
                          <span
                            style={{
                              fontSize: compact ? '9px' : '11px',
                              color: theme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Nowcast
                          </span>
                          <Switch
                            size="small"
                            checked={liveEndMinutesForRadar !== 0}
                            onChange={(checked) => {
                              const newMinutes = checked ? 30 : 0;
                              onLiveEndMinutesForRadarChange(newMinutes);
                              if (selectedRange && onDateRangeChange) {
                                const now = new Date();
                                const newEnd = new Date(now.getTime() + newMinutes * 60 * 1000);
                                const start = selectedRange.start;
                                setSelectedRange({ start, end: newEnd });
                                onDateRangeChange(start, newEnd);
                              }
                            }}
                          />
                        </div>
                      </Tooltip>
                    )}

                    {isTioProvider(mapTileProvider) && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? '1px' : '2px', alignItems: 'center' }}>
                        <label
                          style={{
                            fontSize: compact ? '8px' : '9px',
                            color: theme === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                          }}
                        >
                          To (MIN)
                        </label>
                        <InputNumber
                          size="small"
                          min={1}
                          max={60}
                          value={pendingEndMinutes ?? undefined}
                          onChange={(value) => {
                            setPendingEndMinutes(
                              value === null ? null : Math.max(1, Math.min(60, value))
                            );
                          }}
                          style={{ width: compact ? '40px' : '50px', fontSize: compact ? '9px' : '11px' }}
                          controls={false}
                          formatter={(value) => `${value}`}
                          parser={(value) => {
                            const parsed = parseInt(value?.replace(/[^\d]/g, '') || '1', 10);
                            return Math.max(1, Math.min(60, parsed));
                          }}
                        />
                      </div>
                    )}

                    <Tooltip title="Apply live range settings">
                      <Button
                        type="primary"
                        size="small"
                        icon={<CheckOutlined />}
                        disabled={
                          pendingStartMinutes === null ||
                          (isTioProvider(mapTileProvider) && pendingEndMinutes === null) ||
                          (pendingStartMinutes === liveStartMinutes &&
                            (!isTioProvider(mapTileProvider) ||
                              (pendingEndMinutes !== null && pendingEndMinutes === liveEndMinutes)))
                        }
                        onClick={() => {
                          if (pendingStartMinutes === null) return;
                          onLiveStartMinutesChange?.(pendingStartMinutes);
                          if (isTioProvider(mapTileProvider) && onLiveEndMinutesChange && pendingEndMinutes !== null) {
                            onLiveEndMinutesChange(pendingEndMinutes);
                          }
                          if (isLiveMode && onDateRangeChange) {
                            const now = new Date();
                            const startMinutes = Math.max(liveStartMinLimit, Math.min(0, pendingStartMinutes));
                            const endMinutes =
                              isTioProvider(mapTileProvider)
                                ? Math.max(1, Math.min(60, pendingEndMinutes ?? liveEndMinutes))
                                : Math.max(0, Math.min(60, liveEndMinutesForRadar));
                            const start = new Date(
                              now.getFullYear(),
                              now.getMonth(),
                              now.getDate(),
                              now.getHours(),
                              now.getMinutes() + startMinutes,
                              0,
                              0
                            );
                            const end = new Date(now.getTime() + endMinutes * 60 * 1000);
                            setSelectedRange({ start, end });
                            onDateRangeChange(start, end);
                          }
                        }}
                        style={{ height: compact ? '20px' : '24px', padding: compact ? '0 4px' : '0 8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      />
                    </Tooltip>
                  </div>
                </Tooltip>

                <Tooltip
                  title={
                    isLiveMode
                      ? `Live mode: Range updates automatically every minute (${liveStartMinutes} min to ${isTioProvider(mapTileProvider) ? `+${liveEndMinutes} min` : liveEndMinutesForRadar === 0 ? 'now' : `+${liveEndMinutesForRadar} min`})`
                      : 'Enable live mode to track current time range'
                  }
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: compact ? '3px' : '6px',
                      padding: compact ? '2px 4px' : '4px 8px',
                      borderRadius: '4px',
                      backgroundColor: liveToggleBg,
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      if (!isLiveMode) handleEnableLiveRange();
                      setIsLiveMode(!isLiveMode);
                    }}
                  >
                    <span style={liveIndicatorStyle} />
                    <span style={liveLabelStyle}>LIVE</span>
                    <Switch
                      size="small"
                      checked={isLiveMode}
                      onChange={(checked) => {
                        if (checked) handleEnableLiveRange();
                        setIsLiveMode(checked);
                      }}
                      style={{ marginLeft: '2px' }}
                    />
                  </div>
                </Tooltip>
              </>
            )}
          </div>
        )}

        {mode === 'point' && selectedPoint && (
          <div style={{ display: 'flex', alignItems: 'center', gap: compact ? '4px' : '8px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? '1px' : '2px' }}>
              <Button
                icon={<PlusOutlined />}
                size="small"
                type="text"
                onClick={() => adjustStartTime(1)}
                style={btnStyle}
                title="Add 1 minute"
              />
              <Button
                icon={<MinusOutlined />}
                size="small"
                type="text"
                onClick={() => adjustStartTime(-1)}
                style={btnStyle}
                title="Subtract 1 minute"
              />
            </div>

            <span style={timeDisplayStyle}>
              {formatCalendarDate(selectedPoint)}
              <span style={{ opacity: 0.6, marginLeft: '4px', fontSize: compact ? '8px' : '10px' }}>
                ({selectedEvent?.circuit?.time_zone?.split('/')[1] || 'Local'})
              </span>
            </span>

            {isEventActive && (
              <Tooltip
                title={
                  isLiveMode
                    ? 'Live mode: Time updates automatically every minute'
                    : 'Enable live mode to track current time'
                }
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: compact ? '3px' : '6px',
                    padding: compact ? '2px 4px' : '4px 8px',
                    borderRadius: '4px',
                    backgroundColor: liveToggleBg,
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    if (!isLiveMode) handleEnableLivePoint();
                    setIsLiveMode(!isLiveMode);
                  }}
                >
                  <span style={liveIndicatorStyle} />
                  <span style={liveLabelStyle}>LIVE</span>
                  <Switch
                    size="small"
                    checked={isLiveMode}
                    onChange={(checked) => {
                      if (checked) handleEnableLivePoint();
                      setIsLiveMode(checked);
                    }}
                    style={{ marginLeft: '2px' }}
                  />
                </div>
              </Tooltip>
            )}
          </div>
        )}

        {onRefresh && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: 'auto' }}>
            <Tooltip title="Refresh">
              <Button
                icon={<ReloadOutlined spin={refreshing} />}
                onClick={onRefresh}
                size="small"
                type="text"
              />
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  );
};
